import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import posterSymbol from "./assets/redesign/performance-default.png";

const DEFAULT_POSTER_WIDTH = 375;
const DEFAULT_POSTER_HEIGHT = 500;
const DEFAULT_POSTER_FONT_SIZE = 72;
const DEFAULT_POSTER_LINE_HEIGHT = 58 / 48;
const DEFAULT_POSTER_SYMBOL_SIZE = 240;

function PerformanceDefaultImage({
  title,
  className,
}: {
  title?: string | null;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const symbolRef = useRef<HTMLImageElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);

  const fitTitle = useCallback(() => {
    const container = containerRef.current;
    const symbol = symbolRef.current;
    const titleElement = titleRef.current;
    if (!container || !symbol) return;

    const width = container.clientWidth;
    const height = container.clientHeight;
    if (!width || !height) return;

    const widthRatio = width / DEFAULT_POSTER_WIDTH;
    const heightRatio = height / DEFAULT_POSTER_HEIGHT;
    const scale = Math.min(widthRatio, heightRatio);
    const horizontalPadding = 16 * widthRatio;
    const availableWidth = Math.max(0, width - horizontalPadding * 2);
    const availableHeight = height;
    // ITDb와 동일한 카드 폭 비례 크기 및 긴 제목의 높이 맞춤 축소
    const maxFontSize = DEFAULT_POSTER_FONT_SIZE * widthRatio;
    const minFontSize = Math.min(maxFontSize, Math.max(8, maxFontSize * 0.35));

    container.style.padding = `0 ${horizontalPadding}px`;
    symbol.style.width = `${DEFAULT_POSTER_SYMBOL_SIZE * scale}px`;
    symbol.style.height = `${DEFAULT_POSTER_SYMBOL_SIZE * scale}px`;
    symbol.style.top = `${height / 2 - 16 * scale}px`;
    if (!titleElement) return;

    titleElement.style.display = "block";
    titleElement.style.width = `${availableWidth}px`;
    titleElement.style.webkitLineClamp = "unset";

    let low = minFontSize;
    let high = maxFontSize;
    for (let index = 0; index < 10; index += 1) {
      const mid = (low + high) / 2;
      titleElement.style.fontSize = `${mid}px`;
      titleElement.style.lineHeight = `${DEFAULT_POSTER_LINE_HEIGHT}`;
      if (
        titleElement.scrollWidth <= availableWidth + 1 &&
        titleElement.scrollHeight <= availableHeight + 1
      ) {
        low = mid;
      } else {
        high = mid;
      }
    }
    titleElement.style.fontSize = `${low}px`;
    titleElement.style.display = "-webkit-box";
    titleElement.style.webkitBoxOrient = "vertical";
    titleElement.style.webkitLineClamp = `${Math.max(
      1,
      Math.floor(availableHeight / (low * DEFAULT_POSTER_LINE_HEIGHT)),
    )}`;
  }, []);

  useLayoutEffect(() => {
    fitTitle();
  }, [fitTitle, title]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(fitTitle);
    observer?.observe(container);
    document.fonts?.addEventListener("loadingdone", fitTitle);
    void document.fonts?.ready.then(fitTitle);
    return () => {
      observer?.disconnect();
      document.fonts?.removeEventListener("loadingdone", fitTitle);
    };
  }, [fitTitle]);

  const normalizedTitle = title?.trim().toUpperCase();

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={normalizedTitle || "Performance poster"}
      style={{ backgroundColor: "#EFEFF7" }}
      className={`profile-default-poster ${className || ""}`}
    >
      <img
        ref={symbolRef}
        src={posterSymbol}
        alt=""
        className="profile-default-poster-symbol"
        draggable={false}
      />
      {normalizedTitle && (
        <span
          ref={titleRef}
          className="profile-default-poster-title"
        >
          {normalizedTitle}
        </span>
      )}
    </div>
  );
}

export function PerformancePoster({ src, title, posterImageType }: { src?: string; title: string; posterImageType?: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src, posterImageType]);
  if (!src || failed || posterImageType?.trim().toLowerCase() === "default") {
    return <PerformanceDefaultImage title={title} />;
  }
  return <img className="profile-work-image" src={src} alt={title} loading="lazy" draggable={false} onError={() => setFailed(true)} />;
}
